import { useState } from 'react';
import type { Timestamp } from 'firebase/firestore';

interface ApprovalData {
  approved: boolean;
  approvedAt: Timestamp | Date | null;
  approvedBy: string | null;
  note: string | null;
}

export interface ApprovalSavePayload {
  state: 'approved' | 'rejected' | 'cleared';
  note: string | null;
}

interface Props {
  approval: ApprovalData | null;
  onSave: (payload: ApprovalSavePayload) => void | Promise<void>;
}

type RadioState = 'cleared' | 'approved' | 'rejected';

function initialState(approval: ApprovalData | null): RadioState {
  if (!approval) return 'cleared';
  return approval.approved ? 'approved' : 'rejected';
}

export default function AdminApprovalSection({ approval, onSave }: Props) {
  const [state, setState] = useState<RadioState>(initialState(approval));
  const [note, setNote] = useState<string>(approval?.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setError(null);
    if (state === 'rejected' && note.trim().length === 0) {
      setError('Anteckning krävs vid avslag.');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        state,
        note: state === 'cleared' ? null : note.trim() || null,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <fieldset className="space-y-3 border-t pt-4">
      <legend className="font-bold">Approval (admin)</legend>
      <div className="flex gap-4 flex-wrap">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="approval-state"
            checked={state === 'cleared'}
            onChange={() => setState('cleared')}
          />
          Ej granskad
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="approval-state"
            checked={state === 'approved'}
            onChange={() => setState('approved')}
          />
          Godkänn
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="approval-state"
            checked={state === 'rejected'}
            onChange={() => setState('rejected')}
          />
          Avslå
        </label>
      </div>
      <label className="block text-sm">
        Anteckning {state === 'rejected' && <span className="text-red-700">*</span>}
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          className="mt-1 w-full rounded border-gray-300 shadow-sm focus:border-sky focus:ring-sky"
        />
      </label>
      {error && <p className="text-sm text-red-700">{error}</p>}
      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="px-4 py-2 bg-navy text-white rounded disabled:opacity-50"
      >
        {saving ? 'Sparar…' : 'Spara approval'}
      </button>
      {approval?.approvedBy && (
        <p className="text-xs text-gray-600">
          Senast ändrad av {approval.approvedBy}
        </p>
      )}
    </fieldset>
  );
}
